import { PermissionError } from '../error/error';
import { navigationRef } from '../navigation/navigation.ref';
import { useSelectedBusinessUserStore } from '../screens/home/selectedBusinessUser.store';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { getAuthStore, useAuthContext } from '../screens/auth/auth.utils';
import api from './api';
import { API_HOST_URL } from './api.const';

export const fetchQuery = <T>(url: string, actingUser = true): Partial<UseQueryOptions> => ({
  queryKey: [url],
  queryFn: async () => {
    try {
      const store = getAuthStore();
      const selectedUserId =
        useSelectedBusinessUserStore.getState().selectedOwnerId ||
        useSelectedBusinessUserStore.getState().selectedUserId;

      const [response] = await api(
        `${API_HOST_URL}/${url}`,
        { method: 'GET' },
        {
          ...(store?.webAuth?.accessToken && {
            Authorization: `Bearer ${store.webAuth.accessToken}`,
            ...(actingUser && selectedUserId && { 'X-Acting-User': selectedUserId }),
          }),
        },
      );

      return response.data as T;
    } catch (err) {
      if (err instanceof PermissionError) {
        navigationRef.current?.navigate('Connect', { refresh: true });
      }
    }
  },
  staleTime: 10000,
});

export type Opts = Omit<UseQueryOptions, 'queryFn' | 'queryKey'> & Partial<Pick<UseQueryOptions, 'queryKey'>>;

export const useReactQuery =
  <T>(url: string) =>
  (opts?: Opts) =>
    useQuery({ ...fetchQuery<T>(url), ...opts } as UseQueryOptions<T>);

export const useAdminReactQuery =
  <T>(url: string) =>
  (opts?: Opts) =>
    useQuery({ ...fetchQuery<T>(url, false), ...opts } as UseQueryOptions<T>);

export const getRequestHeaders = ({ ignoreActingUser }: { ignoreActingUser?: boolean } = {}) => {
  const { webAuth } = useAuthContext();
  const selectedUserId =
    useSelectedBusinessUserStore.getState().selectedOwnerId ||
    useSelectedBusinessUserStore.getState().selectedUserId;

  return {
    ...(webAuth && { Authorization: `Bearer ${webAuth!.accessToken}` }),
    ...(selectedUserId && !ignoreActingUser && { 'X-Acting-User': selectedUserId }),
  };
};
